import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { buildSpeakableSchema, SCHEMA_PERSON_ID, SCHEMA_ORG_ID } from "@/lib/entitySchema";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";
import { TrendingUp, Bot, MapPin, Sparkles, ArrowRight } from "lucide-react";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/colorado-seo-trends-2026";
const TITLE = "Colorado SEO Trends 2026 | What's Actually Working This Year";
const DESCRIPTION =
  "The 2026 Colorado SEO field report — what's moving the Map Pack, what AI Overviews changed, and the local SEO tactics still earning calls. Updated quarterly by an owner-operated Colorado agency.";
const PUBLISHED = "2026-01-15";
const UPDATED = "2026-04-30";

const TRENDS = [
  {
    title: "AI Overviews now answer 41% of Colorado service queries",
    body: "Google's AI Overview shows above the Map Pack on roughly 4 in 10 'near me' searches across Colorado markets we audit weekly. The cited sources are almost never the #1 organic result — they're the pages with concise FAQ blocks, comparison tables, and clear local entity schema. If your service pages still read like a brochure, you're invisible to AI Overviews.",
  },
  {
    title: "Map Pack now favors recency over review count",
    body: "Through Q1 2026 we've watched profiles with 50 reviews outrank profiles with 500 — when the smaller profile posts weekly, geo-tags photos, and answers Q&A within 24 hours. Static GBPs (no posts in 90+ days) are losing top-3 positions even with strong review velocity historically.",
  },
  {
    title: "GEO (Generative Engine Optimization) is now its own line item",
    body: "Half of new Colorado SEO RFPs we see in 2026 explicitly ask for GEO scope — schema, llms.txt, AI-extractable answer blocks. A year ago this was novel; now it's table stakes. Agencies still selling 'just SEO' are quietly losing renewals.",
  },
  {
    title: "Citation directories are deprioritized — entity sameAs links replace them",
    body: "Yelp, YP, Manta and the citation tier-3 directories carry less weight than verified entity-graph signals (LinkedIn, BBB, GBP, real press). Building a coherent sameAs graph in your Organization schema now outpaces buying citation packages.",
  },
  {
    title: "Local content needs neighborhood-level specificity to convert",
    body: "Generic city pages ('SEO in Denver') still rank, but neighborhood pages ('SEO for Cherry Creek businesses') convert at 2-3× the rate. Buyers searching with neighborhood modifiers have higher commercial intent.",
  },
  {
    title: "Speed matters less than INP — Core Web Vitals shifted",
    body: "Interaction-to-Next-Paint (INP) replaced FID in 2024 and is now a clearer ranking signal in 2026 than LCP for service-business sites. Heavy chat widgets, Elfsight reviews, and 3rd-party trackers loaded eagerly are silent ranking killers.",
  },
  {
    title: "Year-stamped content dominates AI Overviews and Featured Snippets",
    body: "Pages with the year in the title and updated dateModified schema get cited 3-5× more often than evergreen pages. ChatGPT and Perplexity prefer recent sources by design.",
  },
];

const PREDICTIONS = [
  "Q3 2026: AI Overviews will answer >55% of CO service queries; Map Pack will appear lower on the page on mobile.",
  "Q4 2026: Google will roll out an explicit 'GEO performance' tab in Search Console (we expect — not confirmed).",
  "End of 2026: 'Owner-led / local-only' positioning will out-convert national agency claims by 2:1 in Colorado markets.",
];

export const Route = createFileRoute("/colorado-seo-trends-2026")({
  head: () => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      mainEntityOfPage: `${SITE_URL}${PATH}`,
      headline: TITLE,
      description: DESCRIPTION,
      image: `${SITE_URL}/logo.png`,
      datePublished: PUBLISHED,
      dateModified: UPDATED,
      author: { "@id": SCHEMA_PERSON_ID },
      publisher: { "@id": SCHEMA_ORG_ID },
      about: ["Colorado SEO", "Local SEO", "Google Map Pack", "AI Overviews", "Generative Engine Optimization"],
    };
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "article" },
        { property: "article:published_time", content: PUBLISHED },
        { property: "article:modified_time", content: UPDATED },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ],
      links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(schema) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`${SITE_URL}${PATH}`, TITLE)) },
        buildBreadcrumbScript([
          { name: "Home", path: "/" },
          { name: "Insights", path: "/learn" },
          { name: "Colorado SEO Trends 2026", path: PATH },
        ]),
      ],
    };
  },
  component: TrendsPage,
});

function TrendsPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Colorado SEO Field Report · 2026"
        title={
          <>
            What's actually working in <span className="text-gradient-gold">Colorado SEO</span> in 2026
          </>
        }
        description="Seven trends from inside a Colorado agency that ran SEO for 30+ Colorado service businesses through Q1 2026. Updated quarterly."
      />

      <section className="bg-background py-10 lg:py-14">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Last reviewed: {UPDATED} · Originally published {PUBLISHED}
          </p>
          <p className="mt-4 text-base leading-relaxed text-foreground" data-speakable>
            The Colorado SEO landscape shifted hard between Q4 2024 and Q1 2026. AI
            Overviews moved from novelty to default, the Map Pack rebalanced
            around freshness, and entity-graph SEO replaced citation building as
            the primary off-site lever. Here's what we're seeing on actual client
            campaigns this year.
          </p>
        </div>
      </section>

      <section className="bg-background pb-12 lg:pb-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 space-y-8">
          {TRENDS.map((t, i) => (
            <article key={t.title} className="rounded-2xl border border-border bg-card p-6 lg:p-8">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-gold/15 text-sm font-extrabold text-brand-gold-deep">
                  {i + 1}
                </div>
                <div>
                  <h2 className="text-xl font-extrabold text-foreground lg:text-2xl">{t.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t.body}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-section-alt py-12 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="flex items-center gap-2 text-2xl font-extrabold text-foreground">
            <TrendingUp className="h-6 w-6 text-brand-gold-deep" />
            Our 2026 Colorado SEO predictions
          </h2>
          <ul className="mt-6 space-y-3">
            {PREDICTIONS.map((p) => (
              <li key={p} className="flex gap-3 rounded-lg border border-border bg-background p-4 text-sm text-foreground">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-deep" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-background py-12 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <h2 className="text-2xl font-extrabold text-foreground">
            Want this analysis applied to your business?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
            We'll audit your current SEO + Map Pack + AI Overview presence against the 2026 baseline and tell you where to invest.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/free-audit" className="inline-flex items-center gap-2 rounded-lg bg-brand-gold-deep px-5 py-3 text-sm font-semibold text-background transition-smooth hover:bg-brand-gold">
              Request a free 2026 audit
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/geo-ai" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/60">
              <Bot className="h-4 w-4" />
              See our GEO AI service
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
